import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import { forwardRef, useImperativeHandle, useState } from 'react';
import PaymentManager from '../../../../services/api/PaymentManager';
import { PaymentMethod } from '../../../../services/exports/Constants';
import { Modals } from '../../exports/Enums';
import useUrlParams from '../../../../hooks/utility/useUrlParams';
import toast from 'react-hot-toast';
import { IonContent, IonSkeletonText } from '@ionic/react';
import { StripeError } from '@stripe/stripe-js';
import { IModal } from '../../router/ModalRouter';
import useEffectOnce from '../../common/hooks/useEffectOnce';
import { useTranslation } from 'react-i18next';
import WindowResource from '../../../../services/resources/WindowResource';

export interface ImperativeAddCard {
  addCard: () => Promise<void>;
}

const Content = forwardRef<ImperativeAddCard, Partial<IModal>>((props, ref) => {
  const { t } = useTranslation(null, {
    keyPrefix:
      'Modules:Mobile:Features:Profile:Components:ProfileModal:NestedModals:PaymentMethods:AddCreditCard:AddCreditCardModal',
  });
  const { goBack } = props;
  const stripe = useStripe();
  const elements = useElements();
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { setup_intent_client_secret } = useUrlParams();
  const [loading, setLoading] = useState<boolean>(!!setup_intent_client_secret);

  useImperativeHandle(ref, () => {
    return {
      addCard: async () => {
        await addCard();
      },
    };
  }, [stripe, elements]);

  const getSetupIntent = async (): Promise<void> => {
    if (!setup_intent_client_secret) return;
    if (!stripe) return;
    try {
      setLoading(true);
      const setupIntentResponse = await stripe.retrieveSetupIntent(setup_intent_client_secret);
      const { payment_method } = setupIntentResponse.setupIntent;
      const { success } = await PaymentManager.createPaymentOption({
        company_id: company?.id,
        payment_method_id: PaymentMethod.Card,
        external_payment_method_id: payment_method,
      });
      if (success) {
        new Promise((resolve) => {
          window.history.replaceState({}, document.title, window.location.pathname);
          resolve(true);
        })
          .then(() => {
            toast.success(t('feedback.success'));
            goBack();
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  useEffectOnce(() => {
    getSetupIntent();
  }, !!stripe);

  const addCard = async (): Promise<never | { error: StripeError }> => {
    if (!stripe || !elements) {
      return;
    }

    return await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${WindowResource.getOriginWithPathname()}?open_modal=${Modals.AddCreditCard}&mobile_app=true`,
      },
    });
  };

  const renderAddCard = (): JSX.Element => {
    return (
      <div className="p-sm border bg-[#F5F5F5]">
        <PaymentElement
          options={{
            wallets: {
              applePay: 'never',
              googlePay: 'never',
            },
            fields: {
              billingDetails: {
                email: 'auto',
                name: 'auto',
                phone: 'auto',
                address: 'auto',
              },
            },
            defaultValues: {
              billingDetails: {
                email: profile?.email,
                name: `${profile?.first_name} ${profile?.last_name}`,
                phone: profile?.phone_number,
                address: {
                  country: profile?.country,
                  postal_code: profile?.zip_code,
                  state: profile?.state,
                  city: profile?.city,
                  line1: profile?.address,
                },
              },
            },
          }}
        />
      </div>
    );
  };

  const renderContent = (): JSX.Element => {
    if (loading)
      return (
        <div className="flex flex-col gap-4 p-sm">
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
          <IonSkeletonText animated={true} className="w-full h-6" />
        </div>
      );

    return renderAddCard();
  };

  return <IonContent className="min-h-[75dvh] [&::part(scroll)]:py-4 space-y-md">{renderContent()}</IonContent>;
});

export default Content;
